import React from 'react';
import {
  UserOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  CalendarOutlined,
  ClearOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { Table, TableStatus } from '../../types/tables';

// Past this point an "occupied" table almost certainly means someone forgot to close it —
// a real meal runs 1-3h. The card switches to the alert ("¿olvidada?") treatment.
export const STALE_THRESHOLD_MINUTES = 4 * 60;

export const statusLabelMap: Record<TableStatus, string> = {
  Available: 'Disponible',
  Occupied: 'Ocupada',
  PreCheck: 'Precuenta',
  Reserved: 'Reservada',
  Dirty: 'Sucia',
};

export const statusColorMap: Record<TableStatus, string> = {
  Available: 'success',
  Occupied: 'error',
  PreCheck: 'warning',
  Reserved: 'purple',
  Dirty: 'default',
};

export const statusClassMap: Record<TableStatus, string> = {
  Available: 'available',
  Occupied: 'occupied',
  PreCheck: 'precheck',
  Reserved: 'reserved',
  Dirty: 'dirty',
};

export const getStatusIcon = (status: TableStatus): React.ReactNode => {
  switch (status) {
    case 'Available': return <UserOutlined />;
    case 'Occupied': return <ClockCircleOutlined />;
    case 'PreCheck': return <FileTextOutlined />;
    case 'Reserved': return <CalendarOutlined />;
    case 'Dirty': return <ClearOutlined />;
    default: return <UserOutlined />;
  }
};

export const elapsedMinutes = (lastActivity: string | undefined): number | null => {
  if (!lastActivity) return null;
  return Math.max(0, dayjs().diff(dayjs(lastActivity), 'minute'));
};

export const isStaleTable = (table: Table): boolean => {
  if (table.status !== 'Occupied') return false;
  const minutes = elapsedMinutes(table.lastActivity);
  return minutes !== null && minutes >= STALE_THRESHOLD_MINUTES;
};

/** "45 min", "1h 20m" — past the stale threshold "+17h", and past 48h "+29d". */
export const formatElapsedTime = (lastActivity: string | undefined): string => {
  const minutes = elapsedMinutes(lastActivity);
  if (minutes === null) return '';
  if (minutes >= 48 * 60) return `+${Math.floor(minutes / (24 * 60))}d`;
  if (minutes >= STALE_THRESHOLD_MINUTES) return `+${Math.floor(minutes / 60)}h`;
  if (minutes < 1) return '< 1 min';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  if (remainingMins === 0) return `${hours}h`;
  return `${hours}h ${remainingMins}m`;
};

/** "$1,240" — floor-map money is scanned, not audited; cents are noise there. */
export const formatMoneyCompact = (amount: number): string =>
  `$${Math.round(amount).toLocaleString('en-US')}`;

export interface FloorStats {
  occupied: number;
  preCheck: number;
  available: number;
  openTotal: number;
}

export const computeFloorStats = (tables: Table[]): FloorStats => {
  const stats: FloorStats = { occupied: 0, preCheck: 0, available: 0, openTotal: 0 };
  for (const table of tables) {
    if (table.status === 'Occupied') stats.occupied++;
    else if (table.status === 'PreCheck') stats.preCheck++;
    else if (table.status === 'Available') stats.available++;
    if (table.currentOrderTotal) stats.openTotal += table.currentOrderTotal;
  }
  return stats;
};

export interface AreaOccupancy {
  occupied: number;
  total: number;
}

export const computeAreaOccupancy = (tables: Table[]): Record<string, AreaOccupancy> => {
  const byArea: Record<string, AreaOccupancy> = {};
  for (const table of tables) {
    const entry = byArea[table.areaId] ?? { occupied: 0, total: 0 };
    entry.total++;
    if (table.status === 'Occupied' || table.status === 'PreCheck') entry.occupied++;
    byArea[table.areaId] = entry;
  }
  return byArea;
};

// Vista compacta: el círculo de la mesa mide ~60px, así que más de 4 caracteres ya no caben
// a un tamaño legible. Ahí se muestran iniciales + número y el nombre completo va debajo.
const SHORT_NAME_MAX = 4;
const NAME_CONNECTORS = new Set(['de', 'del', 'la', 'el', 'los', 'las', 'y']);

/**
 * "Sala Ejecutiva 2" → "SE2", "Cava de Vinos" → "CV", "Jardín 1" → "J1", "Mesa 12" → "12".
 * Hasta 4 caracteres se respeta tal cual ("B1", "12", "Cava"), y el número nunca se recorta
 * ("10001", "Patio 12B" → "P12B"): abreviar un número lo vuelve otra mesa.
 */
export const abbreviateTableName = (name: string): string => {
  const trimmed = name.trim();
  if (trimmed.length <= SHORT_NAME_MAX || /^\d+$/.test(trimmed)) return trimmed;

  const words = trimmed.split(/\s+/).filter(w => !NAME_CONNECTORS.has(w.toLowerCase()));
  // "Mesa" no distingue a nadie en un mapa de mesas: "Mesa 12" es la 12.
  if (words.length > 1 && words[0].toLowerCase() === 'mesa') words.shift();
  if (words.length === 0) return trimmed.slice(0, 3);

  // La última palabra es "el número" si trae algún dígito: 12, 12B, B2.
  const last = words[words.length - 1];
  const number = words.length > 1 && /\d/.test(last) ? words.pop()! : '';

  if (words.length === 1 && !number) {
    const word = words[0];
    if (word.length <= SHORT_NAME_MAX) return word;
    // "Terraza12" → "T12", "Terraza12B" → "T12B"
    const glued = /^(\D+?)(\d.*)$/.exec(word);
    if (glued) return glued[1][0].toUpperCase() + glued[2];
    return word.slice(0, 3);
  }

  return words.map(w => w[0].toUpperCase()).join('').slice(0, 3) + number;
};
